import { useState, useCallback, useRef, useEffect } from 'react';
import { ethers } from 'ethers';
import {
  quoteBridgeFee,
  executeBridge,
  getChainPair,
  switchToChain,
  isSogniSessionWallet,
  createSessionSignerForChain,
  pollLzScanStatus,
} from '../services/bridgeService';

/**
 * Hook managing NFT bridge selection, fee quoting, and sequential execution.
 *
 * @param {Object}   opts
 * @param {string}   opts.account          - Connected wallet address
 * @param {Object}   opts.signer           - ethers Signer
 * @param {Function} opts.onRequireLogin   - Callback when login is needed
 * @param {Function} opts.onBridgeStateChange - Called with (bool) when bridging starts/ends
 * @param {Function} opts.refreshNFTs      - Callback to re-fetch NFT list after bridge
 */
export function useBridge({ account, signer, onRequireLogin, onBridgeStateChange, refreshNFTs }) {
  // ---- selection state ----
  const [bridgeSelectMode, setBridgeSelectMode] = useState(false);
  const [selectedNFTs, setSelectedNFTs] = useState(new Map()); // key: "chain-tokenId"
  const [sourceChain, setSourceChain] = useState(null); // 'Base' | 'Etherlink'

  // ---- fee state ----
  const [totalEstimatedFee, setTotalEstimatedFee] = useState(null);
  const [feeQuoting, setFeeQuoting] = useState(false);
  const [feeError, setFeeError] = useState(null);
  const quoteTimer = useRef(null);

  // ---- modal / execution state ----
  const [showBridgeModal, setShowBridgeModal] = useState(false);
  const [bridgeProgress, setBridgeProgress] = useState([]); // [{ tokenId, chain, status, txHash, error }]
  const [isBridging, setIsBridging] = useState(false);
  const [bridgeComplete, setBridgeComplete] = useState(false);
  const cancelledRef = useRef(false);

  // ---- auto-start after reauth ----
  const pendingStartRef = useRef(false);

  // ---- LZ delivery tracking ----
  const [lzStatuses, setLzStatuses] = useState({}); // { txHash: { status, message } }

  // ---- refresh trigger for MyNFTs ----
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // ---- optimistic removal of bridged source-chain NFTs ----
  const [bridgedTokenKeys, setBridgedTokenKeys] = useState(new Set());

  // Derived
  const destinationChain = sourceChain === 'Etherlink' ? 'Base' : 'Etherlink';
  const { source: sourceConfig, destination: destConfig } = sourceChain
    ? getChainPair(sourceChain)
    : { source: null, destination: null };
  const nativeCurrencySymbol = sourceConfig?.nativeCurrency?.symbol || 'ETH';

  // ---- selection helpers ----
  const toggleNFTSelection = useCallback(
    (nft) => {
      setSelectedNFTs((prev) => {
        const key = `${nft.chain || 'Base'}-${nft.tokenId}`;
        const next = new Map(prev);

        if (next.has(key)) {
          next.delete(key);
          // If nothing left, reset source chain lock
          if (next.size === 0) setSourceChain(null);
        } else {
          const chain = nft.chain || 'Base';
          // First selection locks the source chain
          if (prev.size === 0) setSourceChain(chain);
          // Only allow same-chain selections
          if (prev.size > 0 && chain !== sourceChain) return prev;
          next.set(key, nft);
        }
        return next;
      });
    },
    [sourceChain]
  );

  const clearSelection = useCallback(() => {
    setSelectedNFTs(new Map());
    setSourceChain(null);
    setTotalEstimatedFee(null);
    setFeeError(null);
  }, []);

  const selectAllNFTs = useCallback((nftList) => {
    if (!nftList || nftList.length === 0) return;
    // All must be same chain — use the first NFT's chain (or current sourceChain)
    const chain = sourceChain || nftList[0].chain || 'Base';
    const sameChain = nftList.filter((n) => (n.chain || 'Base') === chain);
    const next = new Map();
    sameChain.forEach((nft) => next.set(`${nft.chain || 'Base'}-${nft.tokenId}`, nft));
    setSourceChain(chain);
    setSelectedNFTs(next);
  }, [sourceChain]);

  const exitBridgeMode = useCallback(() => {
    clearSelection();
    setBridgeSelectMode(false);
  }, [clearSelection]);

  // ---- fee quoting (debounced) ----
  useEffect(() => {
    if (quoteTimer.current) clearTimeout(quoteTimer.current);

    if (selectedNFTs.size === 0 || !account) {
      setTotalEstimatedFee(null);
      setFeeError(null);
      return;
    }

    quoteTimer.current = setTimeout(async () => {
      setFeeQuoting(true);
      setFeeError(null);

      try {
        let total = ethers.BigNumber.from(0);
        const nfts = Array.from(selectedNFTs.values());

        // Quote one NFT and multiply (all same chain, fees are nearly identical)
        const sampleFee = await quoteBridgeFee(
          nfts[0].chain || 'Base',
          nfts[0].tokenId,
          account
        );
        // Include gas cost (per tx) + LZ fee (with 10% buffer) for accurate estimate
        const perNft = sampleFee.nativeFee.mul(110).div(100).add(sampleFee.gasCost);
        total = perNft.mul(nfts.length);

        setTotalEstimatedFee(total);
      } catch (err) {
        console.error('Fee quote error:', err);
        setFeeError('Failed to estimate bridge fee');
        setTotalEstimatedFee(null);
      } finally {
        setFeeQuoting(false);
      }
    }, 600);

    return () => {
      if (quoteTimer.current) clearTimeout(quoteTimer.current);
    };
  }, [selectedNFTs, account]);

  // ---- auto-start bridge after reauth provides signer ----
  useEffect(() => {
    if (signer && account && pendingStartRef.current && showBridgeModal) {
      pendingStartRef.current = false;
      // Defer to next tick so React state is fully settled
      const t = setTimeout(() => startBridge(), 0);
      return () => clearTimeout(t);
    }
  }, [signer, account, showBridgeModal]); // startBridge intentionally omitted to avoid loop

  // ---- LZ delivery polling (runs while bridge modal is open) ----
  useEffect(() => {
    if (!bridgeComplete) return;

    const doneItems = bridgeProgress.filter((p) => p.status === 'done' && p.txHash);
    if (doneItems.length === 0) return;

    let cancelled = false;
    const delivered = new Set();

    const poll = async () => {
      for (const item of doneItems) {
        if (delivered.has(item.txHash) || cancelled) continue;
        const result = await pollLzScanStatus(item.txHash);
        if (result && !cancelled) {
          setLzStatuses((prev) => ({ ...prev, [item.txHash]: result }));
          if (result.status === 'DELIVERED' || result.status === 'FAILED') {
            delivered.add(item.txHash);
          }
        }
      }
      // When all delivered/failed, trigger NFT refresh
      if (delivered.size >= doneItems.length && !cancelled) {
        setRefreshTrigger((prev) => prev + 1);
      }
    };

    // Initial poll after 5s (give LZ time to index)
    const initialDelay = setTimeout(poll, 5000);
    // Continue polling every 15s
    const interval = setInterval(() => {
      if (delivered.size < doneItems.length && !cancelled) {
        poll();
      } else {
        clearInterval(interval);
      }
    }, 15000);

    return () => {
      cancelled = true;
      clearTimeout(initialDelay);
      clearInterval(interval);
    };
  }, [bridgeComplete]); // eslint-disable-line

  // ---- bridge execution ----
  const startBridge = useCallback(async () => {
    if (selectedNFTs.size === 0 || !sourceChain) return;

    // If signer/account missing, prompt login and auto-start after reauth
    if (!signer || !account) {
      pendingStartRef.current = true;
      onRequireLogin?.();
      return;
    }

    const nfts = Array.from(selectedNFTs.values());
    const sourceChainKey = sourceChain === 'Etherlink' ? 'etherlink' : 'base';

    cancelledRef.current = false;
    setIsBridging(true);
    setBridgeComplete(false);
    onBridgeStateChange?.(true);

    // Initialize progress
    setBridgeProgress(
      nfts.map((nft) => ({
        tokenId: nft.tokenId,
        chain: nft.chain || 'Base',
        status: 'pending',
        txHash: null,
        error: null,
      }))
    );

    // Ensure correct chain
    let activeSigner = signer;
    try {
      if (isSogniSessionWallet(signer)) {
        // For Sogni wallets, create a signer on the correct chain
        const chainSigner = createSessionSignerForChain(signer, sourceChainKey);
        if (chainSigner) activeSigner = chainSigner;
      } else {
        // External wallet — switch chain
        const rawProvider = signer.provider?.provider;
        if (rawProvider?.request) {
          await switchToChain(rawProvider, sourceChainKey);
          // Wait briefly for provider to update after chain switch
          await new Promise((r) => setTimeout(r, 1000));
        }
      }
    } catch (err) {
      // User rejected chain switch
      setBridgeProgress((prev) =>
        prev.map((p) => ({ ...p, status: 'error', error: 'Chain switch rejected' }))
      );
      setIsBridging(false);
      onBridgeStateChange?.(false);
      return;
    }

    // Sequential execution
    for (let i = 0; i < nfts.length; i++) {
      if (cancelledRef.current) break;
      const nft = nfts[i];

      // Update status: quoting
      setBridgeProgress((prev) =>
        prev.map((p, idx) => (idx === i ? { ...p, status: 'quoting' } : p))
      );

      try {
        // Update status: signing
        setBridgeProgress((prev) =>
          prev.map((p, idx) => (idx === i ? { ...p, status: 'signing' } : p))
        );

        const { tx } = await executeBridge(
          activeSigner,
          nft.chain || 'Base',
          nft.tokenId,
          account
        );

        // Update status: confirming
        setBridgeProgress((prev) =>
          prev.map((p, idx) => (idx === i ? { ...p, status: 'confirming', txHash: tx.hash } : p))
        );

        await tx.wait();

        // Update status: done
        setBridgeProgress((prev) =>
          prev.map((p, idx) => (idx === i ? { ...p, status: 'done', txHash: tx.hash } : p))
        );
      } catch (err) {
        console.error(`Bridge error for token ${nft.tokenId}:`, err);
        const errorMsg = parseBridgeError(err);
        setBridgeProgress((prev) =>
          prev.map((p, idx) => (idx === i ? { ...p, status: 'error', error: errorMsg } : p))
        );
        // Continue with remaining NFTs
      }
    }

    setIsBridging(false);
    setBridgeComplete(true);
    onBridgeStateChange?.(false);
  }, [signer, account, selectedNFTs, sourceChain, onBridgeStateChange, onRequireLogin]);

  // ---- retry a single failed NFT ----
  const retryBridge = useCallback(
    async (tokenId) => {
      if (!signer || !account || !sourceChain) return;

      const idx = bridgeProgress.findIndex((p) => p.tokenId === tokenId);
      if (idx === -1) return;

      const sourceChainKey = sourceChain === 'Etherlink' ? 'etherlink' : 'base';
      let activeSigner = signer;

      if (isSogniSessionWallet(signer)) {
        const chainSigner = createSessionSignerForChain(signer, sourceChainKey);
        if (chainSigner) activeSigner = chainSigner;
      }

      setBridgeProgress((prev) =>
        prev.map((p, i) => (i === idx ? { ...p, status: 'signing', error: null } : p))
      );

      try {
        const { tx } = await executeBridge(
          activeSigner,
          bridgeProgress[idx].chain,
          tokenId,
          account
        );

        setBridgeProgress((prev) =>
          prev.map((p, i) => (i === idx ? { ...p, status: 'confirming', txHash: tx.hash } : p))
        );

        await tx.wait();

        setBridgeProgress((prev) =>
          prev.map((p, i) => (i === idx ? { ...p, status: 'done', txHash: tx.hash } : p))
        );
      } catch (err) {
        console.error(`Retry error for token ${tokenId}:`, err);
        setBridgeProgress((prev) =>
          prev.map((p, i) => (i === idx ? { ...p, status: 'error', error: parseBridgeError(err) } : p))
        );
      }
    },
    [signer, account, sourceChain, bridgeProgress]
  );

  // ---- open modal (pre-populate progress for display) ----
  const openBridgeModal = useCallback(() => {
    const nfts = Array.from(selectedNFTs.values());
    setBridgeProgress(
      nfts.map((nft) => ({
        tokenId: nft.tokenId,
        chain: nft.chain || 'Base',
        status: 'pending',
        txHash: null,
        error: null,
      }))
    );
    setBridgeComplete(false);
    setShowBridgeModal(true);
  }, [selectedNFTs]);

  // ---- close modal and reset ----
  const closeBridgeModal = useCallback(() => {
    setShowBridgeModal(false);
    if (bridgeComplete) {
      // Optimistically hide bridged source-chain NFTs until API catches up
      const keys = new Set(bridgedTokenKeys);
      bridgeProgress
        .filter((p) => p.status === 'done')
        .forEach((p) => keys.add(`${p.chain}-${p.tokenId}`));
      setBridgedTokenKeys(keys);

      clearSelection();
      setBridgeSelectMode(false);
      setBridgeProgress([]);
      setBridgeComplete(false);
      setLzStatuses({});
      setRefreshTrigger((prev) => prev + 1);
      refreshNFTs?.();
    }
  }, [bridgeComplete, bridgeProgress, bridgedTokenKeys, clearSelection, refreshNFTs]);

  return {
    // Selection
    bridgeSelectMode,
    setBridgeSelectMode,
    selectedNFTs,
    toggleNFTSelection,
    selectAllNFTs,
    clearSelection,
    exitBridgeMode,
    sourceChain,
    destinationChain,
    nativeCurrencySymbol,

    // Fees
    totalEstimatedFee,
    feeQuoting,
    feeError,

    // Modal / Execution
    showBridgeModal,
    openBridgeModal,
    bridgeProgress,
    startBridge,
    retryBridge,
    isBridging,
    bridgeComplete,
    closeBridgeModal,

    // LZ delivery tracking
    lzStatuses,

    // Refresh trigger for MyNFTs
    refreshTrigger,

    // Optimistic removal of bridged source-chain NFTs
    bridgedTokenKeys,
  };
}

function parseBridgeError(err) {
  if (err.code === 4001 || err.code === 'ACTION_REJECTED') {
    return 'Transaction rejected';
  }
  const msg = err.reason || err.message || String(err);
  if (msg.includes('Not token owner')) return 'You no longer own this NFT';
  if (msg.includes('blacklisted')) return 'NFT is blacklisted';
  if (msg.includes('Soulbound')) return 'NFT is soulbound';
  if (msg.includes('Bridge not configured')) return 'Bridge not configured';
  if (msg.startsWith('Insufficient ') && msg.includes(' balance'))
    return msg; // Pass through our detailed balance error
  if (msg.includes('insufficient funds') || msg.includes('INSUFFICIENT_FUNDS'))
    return 'Insufficient funds for gas';
  // Truncate long messages
  if (msg.length > 80) return msg.slice(0, 77) + '...';
  return msg;
}
